import React, { useState } from 'react'
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@material-ui/core'
import { Add as AddIcon } from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { toast } from 'react-toastify'

import { useCustomQuestions } from '../../../../hooks/Outreach/useCustomQuestions'
import {
    GuestCardCustomQuestion,
    GuestCardQuestionType,
} from '../../../../models/Outreach/types'
import {
    GUEST_CARD_FIELD_QUESTIONS,
    GuestCardQuestionFieldKey,
} from '../../../../models/Outreach/services'
import { GuestCardFieldEditorChrome } from './GuestCardFieldEditorChrome'

export const QUESTION_TYPE_LABELS: Record<GuestCardQuestionType, string> = {
    SHORT_TEXT: 'Short Text',
    LONG_TEXT: 'Long Text',
    DROPDOWN: 'Dropdown',
    MULTIPLE_CHOICE: 'Multiple Choice',
    YES_NO: 'Yes / No',
    DATE: 'Date',
    NUMBER: 'Number',
}

const useStyles = makeStyles((theme: Theme) => ({
    addRow: {
        marginTop: theme.spacing(2),
        paddingTop: theme.spacing(2),
        borderTop: '1px solid #e5e7eb',
    },
    slot: {
        minHeight: 100,
        marginBottom: theme.spacing(0.5),
        padding: theme.spacing(1.5),
        borderRadius: 8,
        border: '1px dashed #cbd5e1',
        backgroundColor: '#f8fafc',
    },
    sectionTitle: {
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: '#6b7280',
        marginBottom: theme.spacing(1),
        marginTop: theme.spacing(1.5),
        '&:first-child': {
            marginTop: 0,
        },
    },
    item: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        textAlign: 'left',
        padding: theme.spacing(1, 1.25),
        marginBottom: theme.spacing(0.5),
        borderRadius: 8,
        border: '1px solid #e5e7eb',
        backgroundColor: '#fff',
        cursor: 'pointer',
        '&:hover': {
            borderColor: '#3b82f6',
            backgroundColor: '#eff6ff',
        },
    },
    itemText: {
        fontSize: '0.8125rem',
        fontWeight: 500,
        color: '#1f2937',
    },
    optionRow: {
        display: 'flex',
        gap: theme.spacing(1),
        alignItems: 'center',
        marginBottom: theme.spacing(1),
    },
    typeField: {
        minWidth: 160,
        marginTop: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5),
    },
    createActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
    addButton: {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        borderColor: '#3b82f6',
        color: '#3b82f6',
    },
}))

interface AddGuestCardQuestionSlotProps {
    removedDefaultFields: GuestCardQuestionFieldKey[]
    onRestoreDefault: (field: GuestCardQuestionFieldKey) => void
    selectedQuestionIds: number[]
    onAddExistingQuestion: (
        question: GuestCardCustomQuestion,
        required?: boolean,
    ) => void
}

export const AddGuestCardQuestionSlot: React.FC<AddGuestCardQuestionSlotProps> = ({
    removedDefaultFields,
    onRestoreDefault,
    selectedQuestionIds,
    onAddExistingQuestion,
}) => {
    const classes = useStyles()
    const { customQuestions, createCustomQuestion } = useCustomQuestions()
    const [open, setOpen] = useState(false)
    const [mode, setMode] = useState<'pick' | 'create'>('pick')
    const [newQuestionText, setNewQuestionText] = useState('')
    const [
        newQuestionType,
        setNewQuestionType,
    ] = useState<GuestCardQuestionType>('SHORT_TEXT')
    const [newQuestionOptions, setNewQuestionOptions] = useState<string[]>([])
    const [newQuestionRequired, setNewQuestionRequired] = useState(true)
    const [isCreatingQuestion, setIsCreatingQuestion] = useState(false)

    const selected = new Set(selectedQuestionIds)
    const unusedBankQuestions = (customQuestions || []).filter(
        (question) => !selected.has(question.id),
    )
    const needsOptions =
        newQuestionType === 'DROPDOWN' || newQuestionType === 'MULTIPLE_CHOICE'

    const resetCreate = () => {
        setNewQuestionText('')
        setNewQuestionType('SHORT_TEXT')
        setNewQuestionOptions([])
        setNewQuestionRequired(true)
        setMode('pick')
    }

    const closeSlot = () => {
        setOpen(false)
        resetCreate()
    }

    const handleAddQuestionClick = () => {
        if (open) {
            closeSlot()
            return
        }
        setMode('pick')
        setOpen(true)
    }

    const handleCreate = async () => {
        if (!newQuestionText.trim()) {
            toast.error('Question text is required')
            return
        }
        setIsCreatingQuestion(true)
        try {
            const options = needsOptions
                ? newQuestionOptions.filter(
                      (option) => option.trim().length > 0,
                  )
                : undefined
            const response = await createCustomQuestion.mutateAsync({
                question: newQuestionText.trim(),
                question_type: newQuestionType,
                ...(options && options.length > 0 ? { options } : {}),
            })
            const created = (response as { data?: GuestCardCustomQuestion })
                ?.data
            if (created?.id) {
                onAddExistingQuestion(created, newQuestionRequired)
            }
            closeSlot()
        } finally {
            setIsCreatingQuestion(false)
        }
    }

    return (
        <>
            {open && mode === 'pick' && (
                <Box className={classes.slot}>
                    {removedDefaultFields.length > 0 && (
                        <>
                            <Typography className={classes.sectionTitle}>
                                Removed defaults
                            </Typography>
                            {removedDefaultFields.map((field) => (
                                <button
                                    key={field}
                                    type="button"
                                    className={classes.item}
                                    onClick={() => {
                                        onRestoreDefault(field)
                                        closeSlot()
                                    }}
                                >
                                    <Typography className={classes.itemText}>
                                        {GUEST_CARD_FIELD_QUESTIONS[field]}
                                    </Typography>
                                </button>
                            ))}
                        </>
                    )}

                    <Typography className={classes.sectionTitle}>
                        Question bank
                    </Typography>
                    {unusedBankQuestions.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">
                            No unused questions in the bank.
                        </Typography>
                    ) : (
                        unusedBankQuestions.map((question) => (
                            <button
                                key={question.id}
                                type="button"
                                className={classes.item}
                                onClick={() => {
                                    onAddExistingQuestion(question)
                                    closeSlot()
                                }}
                            >
                                <Typography className={classes.itemText}>
                                    {question.question}
                                </Typography>
                            </button>
                        ))
                    )}

                    <Button
                        startIcon={<AddIcon />}
                        onClick={() => setMode('create')}
                        style={{
                            marginTop: 12,
                            textTransform: 'none',
                            color: '#3b82f6',
                        }}
                    >
                        Create new question
                    </Button>
                </Box>
            )}

            {open && mode === 'create' && (
                <GuestCardFieldEditorChrome
                    required={newQuestionRequired}
                    onToggleRequired={setNewQuestionRequired}
                    onRemove={closeSlot}
                >
                    <TextField
                        fullWidth
                        variant="outlined"
                        label="Question"
                        placeholder="Enter question text..."
                        value={newQuestionText}
                        onChange={(e) => setNewQuestionText(e.target.value)}
                    />
                    <FormControl
                        variant="outlined"
                        size="small"
                        className={classes.typeField}
                    >
                        <InputLabel>Type</InputLabel>
                        <Select
                            value={newQuestionType}
                            onChange={(e) => {
                                const value = e.target
                                    .value as GuestCardQuestionType
                                setNewQuestionType(value)
                                if (
                                    value !== 'DROPDOWN' &&
                                    value !== 'MULTIPLE_CHOICE'
                                ) {
                                    setNewQuestionOptions([])
                                } else if (newQuestionOptions.length === 0) {
                                    setNewQuestionOptions([''])
                                }
                            }}
                            label="Type"
                        >
                            {Object.entries(QUESTION_TYPE_LABELS).map(
                                ([value, label]) => (
                                    <MenuItem key={value} value={value}>
                                        {label}
                                    </MenuItem>
                                ),
                            )}
                        </Select>
                    </FormControl>
                    {needsOptions && (
                        <Box style={{ marginBottom: 8 }}>
                            {newQuestionOptions.map((option, idx) => (
                                <Box key={idx} className={classes.optionRow}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        placeholder={`Option ${idx + 1}`}
                                        value={option}
                                        onChange={(e) => {
                                            const updated = [
                                                ...newQuestionOptions,
                                            ]
                                            updated[idx] = e.target.value
                                            setNewQuestionOptions(updated)
                                        }}
                                    />
                                </Box>
                            ))}
                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() =>
                                    setNewQuestionOptions([
                                        ...newQuestionOptions,
                                        '',
                                    ])
                                }
                                style={{
                                    textTransform: 'none',
                                    color: '#3b82f6',
                                }}
                            >
                                Add option
                            </Button>
                        </Box>
                    )}
                    <Box className={classes.createActions}>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleCreate}
                            disabled={
                                isCreatingQuestion || !newQuestionText.trim()
                            }
                            style={{
                                textTransform: 'none',
                                backgroundColor: '#3b82f6',
                                color: 'white',
                            }}
                        >
                            {isCreatingQuestion
                                ? 'Creating...'
                                : 'Create & add'}
                        </Button>
                    </Box>
                </GuestCardFieldEditorChrome>
            )}

            <Box className={classes.addRow}>
                <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    onClick={handleAddQuestionClick}
                    className={classes.addButton}
                >
                    Add question
                </Button>
            </Box>
        </>
    )
}
